import type { SupportedFileTypes } from '@/lib/api';

/** The sentence shown when the server has not answered yet. Deliberately the
 *  same claim the generated list makes, so the dropzone never says nothing. */
export const SUPPORTED_TYPES_FALLBACK =
  'Executables, installers, scripts, and documents. Archives are unpacked so you can pick what runs.';

/** One display row: a group's label and everything it accepts, pre-joined. */
export interface SupportedTypesRow {
  label: string;
  items: string;
}

/** What the platform will detonate, shaped for a label/value grid: one row per
 *  group, one for archives, every extension named. */
export interface SupportedTypesView {
  rows: SupportedTypesRow[];
  note: string;
}

/** Builds the rows from what the server says it accepts rather than a list
 *  kept in step by hand. Returns null before the catalogue has arrived, so the
 *  caller can show the fallback sentence instead of an empty grid. */
export function selectSupportedTypes(data: SupportedFileTypes | undefined): SupportedTypesView | null {
  if (!data || data.groups.length === 0) return null;

  const rows = data.groups.map((g) => ({
    label: g.label,
    items: [...new Set(g.types.flatMap((t) => t.extensions))].join(', '),
  }));

  const archiveFormats = data.archives.formats;
  if (archiveFormats.length === 0) return { rows, note: '' };

  rows.push({ label: data.archives.label, items: archiveFormats.map((f) => f.name).join(', ') });
  return { rows, note: data.archives.note };
}
